import React, { Suspense } from 'react';
import { NormalizedRoute } from 'services/route';
import { Routes, Route, Navigate } from 'react-router-dom';
import { PageError, PageErrorType } from 'pages/page-error';
import { runMiddleware } from './run-middleware';
import { Lazy, LazyStatus } from './lazy.component';
import { HandleMiddlewareError } from './handle-middleware-error';
import { Loading } from 'components/block/loading';
import { useRouter } from 'hooks/use-router.hook';
// import PageNotFound from 'pages/not-found';
import PageNotAccess from 'pages/not-access';
import { useTranslation } from 'react-i18next';

interface MatchRoutesProps {
  routes: NormalizedRoute[];
}

let key = 0;

export let useRouterMemo: {
  pathname: string;
  // Merge params and parsed query string into single "query" object
  // so that they can be used interchangeably.
  // Example: /:topic?sort=popular -> { topic: "react", sort: "popular" }
  query;
  // Include match, location, history objects so we have
  // access to extra React Router functionality if needed.
  navigate;
  location;
};

function displayRoutes(routes: NormalizedRoute[], level = 1, t?: any) {
  const children: any = [];

  routes.forEach((route, index) => {
    key++;
    const { path } = route;
    const keyMatch = `${level}-${index}-${key}`;

    if (route.redirectTo) {
      children.push(<Navigate to={route.redirectTo} />);

      return;
    }

    if (route.middleware) {
      if (route.children) {
        children.push(
          <Route
            key={keyMatch}
            path={path}
            element={
              <Lazy
                onRunning={runMiddleware(route)}
                onComplete={(result) => {
                  if (result === '/page-forbiden') {
                    return (
                      <PageNotAccess
                        pageCode={'403'}
                        title={t('page.error.not.access.title')}
                        desc={t('page.error.not.access.desc')}
                      />
                    );
                  }

                  if (result === '/not-found') {
                    return <PageNotAccess pageCode={'404'} title={t('page.error.not.found.title')} />;
                  }

                  if (result === LazyStatus.RUNNING) {
                    return <route.fallback />;
                  }

                  if (result !== true) {
                    return HandleMiddlewareError(result);
                  }

                  return (
                    <Suspense fallback={<route.fallback />}>
                      <route.component>{displayRoutes(route.children)}</route.component>
                    </Suspense>
                  );
                }}
              />
            }
          >
            {displayRoutes(route.children)}
          </Route>
        );

        return;
      }

      children.push(
        <Route
          key={keyMatch}
          path={path}
          element={
            <Lazy
              onRunning={runMiddleware(route)}
              onComplete={(result) => {
                if (result === LazyStatus.RUNNING) {
                  return <route.fallback />;
                }

                if (result !== true) {
                  return HandleMiddlewareError(result);
                }

                return (
                  <Suspense fallback={<route.fallback />}>
                    <route.component />
                  </Suspense>
                );
              }}
            />
          }
        />
      );

      return;
    }

    if (route.children) {
      children.push(
        <Route key={keyMatch} path={path} element={<route.component />}>
          {displayRoutes(route.children, level++)}
        </Route>
      );

      return;
    }

    children.push(<Route key={keyMatch} path={path} element={<route.component />} />);
  });

  children.push(
    <Route key={`${level}-${key}-p404`} path='' element={<PageError error={PageErrorType.PAGE_NOT_FOUND} />} />
  );

  children.push(
    <Route key={`${level}-${key}-*-p404`} path='*' element={<PageError error={PageErrorType.PAGE_NOT_FOUND} />} />
  );

  return children;
}

export function MatchRoutes({ routes }: MatchRoutesProps) {
  useRouterMemo = useRouter();
  const { t } = useTranslation();

  const routing = displayRoutes(routes, 1, t);

  return (
    <Suspense fallback={<Loading />}>
      <Routes>{routing}</Routes>
    </Suspense>
  );
}
